'use client'

import {UseQueryResult} from '@tanstack/react-query'
import * as React from 'react'
import {useCompanies} from 'src/layouts/main-layout/companies-selector/hooks/use-companies.hook'
import {CompaniesSelectorListType} from 'src/layouts/main-layout/companies-selector/types/companies-selector-list.type'
import {consumeCompaniesSyncFromUrl} from 'src/lib/portal-companies-sync'
import {clearSelectedCompaniesFromClientStorage, readSelectedCompanies, writeSelectedCompanies} from 'src/lib/selected-companies-storage'

type CompaniesSelectorContextValue = {
  search: string | undefined
  setSearch: (filter: string | undefined) => void
  activeTab: string
  setActiveTab: (tab: string) => void
  selectedCompanies: CompaniesSelectorListType[]
  handleSetSelectedCompanies: (item: CompaniesSelectorListType, single?: boolean) => void
  queryCompanies: UseQueryResult<CompaniesSelectorListType[], Error>
  isReady: boolean
}

const CompaniesSelectorContext = React.createContext<CompaniesSelectorContextValue | null>(null)

export type Props = {
  children: React.ReactNode
}

function resolveValidatedSelection(initial: CompaniesSelectorListType[], availableCompanies: CompaniesSelectorListType[]): CompaniesSelectorListType[] {
  if (!availableCompanies.length) return []

  const availableById = new Map(availableCompanies.map((company) => [company.id, company]))
  const validated = initial.filter((company) => availableById.has(company.id)).map((company) => availableById.get(company.id)!)

  if (validated.length) return validated

  return availableCompanies[0] ? [availableCompanies[0]] : []
}

export function CompaniesSelectorProvider({children}: Props) {
  const [search, setSearch] = React.useState<string | undefined>()
  const [activeTab, setActiveTab] = React.useState('switch')
  const [selectedCompanies, setSelectedCompanies] = React.useState<CompaniesSelectorListType[]>([])
  const [isReady, setIsReady] = React.useState(false)

  const queryCompanies = useCompanies()
  const initializedRef = React.useRef(false)

  React.useEffect(() => {
    if (initializedRef.current) return

    const availableCompanies = queryCompanies.data ?? []
    if (!availableCompanies.length) return

    initializedRef.current = true

    const syncedFromPortal = consumeCompaniesSyncFromUrl()
    const storedSelection = syncedFromPortal.length ? syncedFromPortal : readSelectedCompanies()
    const resolvedSelection = resolveValidatedSelection(storedSelection, availableCompanies)

    if (!resolvedSelection.length) {
      clearSelectedCompaniesFromClientStorage()
    } else if (resolvedSelection.length !== storedSelection.length || resolvedSelection.some((company, index) => company.id !== storedSelection[index]?.id)) {
      writeSelectedCompanies(resolvedSelection)
    }

    setSelectedCompanies(resolvedSelection)
    setIsReady(true)
  }, [queryCompanies.data])

  React.useEffect(() => {
    if (!isReady) return
    writeSelectedCompanies(selectedCompanies)
  }, [isReady, selectedCompanies])

  const handleSetSelectedCompanies = (item: CompaniesSelectorListType, single?: boolean) => {
    const exist = selectedCompanies.some((company) => company.id === item.id)

    if (single) {
      setSelectedCompanies([item])
      return
    }
    if (exist && selectedCompanies.length <= 1) return

    if (exist) setSelectedCompanies(selectedCompanies.filter((company) => company.id !== item.id))
    else setSelectedCompanies((prev) => [...prev, item])
  }

  const value = React.useMemo(
    () => ({
      search,
      setSearch,
      activeTab,
      setActiveTab,
      selectedCompanies,
      handleSetSelectedCompanies,
      queryCompanies,
      isReady,
    }),
    [search, setSearch, activeTab, setActiveTab, selectedCompanies, handleSetSelectedCompanies, queryCompanies, isReady],
  )

  return <CompaniesSelectorContext.Provider value={value}>{children}</CompaniesSelectorContext.Provider>
}

export function useCompaniesSelector() {
  const ctx = React.useContext(CompaniesSelectorContext)
  if (!ctx) {
    throw new Error('useCompaniesSelector must be used within <CompaniesSelectorProvider />')
  }
  return ctx
}
